/**
 * @file hooks/useDayTags.js
 * @description React Query hook for per-day tags/badges.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAllDayTags, setDayTag, removeDayTag } from "../services/dayTags";

export function useDayTags(uid) {
  const queryClient = useQueryClient();
  const isGuest = !uid || uid === "guest";

  const tagsQuery = useQuery({
    queryKey: ["dayTags", uid],
    queryFn: () => getAllDayTags(uid),
    enabled: !isGuest,
    staleTime: 1000 * 60 * 5,
  });

  const setTagMutation = useMutation({
    mutationFn: ({ dateKey, tag }) => setDayTag(uid, dateKey, tag),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["dayTags", uid] }),
  });

  const removeTagMutation = useMutation({
    mutationFn: ({ dateKey }) => removeDayTag(uid, dateKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["dayTags", uid] }),
  });

  return {
    tags: tagsQuery.data || {},
    isLoading: tagsQuery.isLoading,
    setTag: (dateKey, tag) => setTagMutation.mutate({ dateKey, tag }),
    removeTag: (dateKey) => removeTagMutation.mutate({ dateKey }),
    isSaving: setTagMutation.isPending || removeTagMutation.isPending,
  };
}
